import { env } from "../Consts/config";
import { getClickConnectorUserHash } from "./api/integrations";

const WIDGET_ID = env.CLICKCONNECTOR_WIDGET_ID || "";
const WIDGET_SCRIPT_ID = "cc-widget-script";

type ClickConnectorWidgetApi = {
  setUser(user: ChatWidgetUser): Promise<void> | void;
  setWidgetVisibility(visible: boolean): void;
};

type WindowWithClickConnector = Window & {
  ccWidget?: ClickConnectorWidgetApi;
};

interface UserHashResponse {
  userId: string;
  userHash: string;
  email: string;
  displayName?: string;
}

// Define the type for the setUser argument according to the SDK documentation or observed usage
interface ChatWidgetUser {
  userId: string;
  userHash: string;
  email: string;
  name: string;
}

interface ChatWidgetWithSetUser {
  setUser(user: ChatWidgetUser): Promise<void> | void;
}

let widgetLoadPromise: Promise<void> | null = null;

const getWidgetApi = (): ClickConnectorWidgetApi | undefined => {
  return (window as WindowWithClickConnector).ccWidget;
};

const loadWidgetScript = async (widgetId: string): Promise<void> => {
  if (getWidgetApi()) return;
  if (widgetLoadPromise) {
    await widgetLoadPromise;
    return;
  }

  widgetLoadPromise = new Promise<void>((resolve, reject) => {
    if (getWidgetApi()) {
      resolve();
      return;
    }

    let script = document.getElementById(WIDGET_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = WIDGET_SCRIPT_ID;
      script.src = `https://widget.clickconnector.app/auto-load.js?widgetId=${encodeURIComponent(
        widgetId
      )}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        widgetLoadPromise = null;
        reject(new Error("Failed to load ClickConnector widget script"));
      };
      document.head.appendChild(script);
    }

    const timeoutMs = 10000;
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (getWidgetApi()) {
        window.clearInterval(interval);
        resolve();
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        widgetLoadPromise = null;
        reject(new Error("Timeout waiting for ClickConnector widget to initialize"));
      }
    }, 150);
  });

  await widgetLoadPromise;
};

const waitForWidgetReady = async (): Promise<ClickConnectorWidgetApi> => {
  await loadWidgetScript(WIDGET_ID);
  const api = getWidgetApi();
  if (!api) {
    throw new Error("ClickConnector widget API is unavailable after loading");
  }
  return api;
};

/**
 * Type guard to validate UserHashResponse objects at runtime.
 */
function isUserHashResponse(obj: unknown): obj is UserHashResponse {
  return (
    obj !== null &&
    typeof obj === "object" &&
    typeof obj.userId === "string" &&
    typeof obj.userHash === "string" &&
    typeof obj.email === "string" &&
    (typeof obj.displayName === "undefined" || typeof obj.displayName === "string")
  );
}

/**
 * Initialize ClickConnector widget with verified user
 * This function should be called when a user is signed in
 */
export const initClickConnectorWidget = async (): Promise<void> => {
  if (!WIDGET_ID) {
    console.warn("ClickConnector widget ID not configured");
    return;
  }

  try {
    // Get user hash from REST API
    const response = await getClickConnectorUserHash();

    if (!response.success || !response.data) {
      console.error("Failed to get user hash from server");
      return;
    }

    if (!isUserHashResponse(response.data)) {
      console.error("API response data is not a valid UserHashResponse", response.data);
      return;
    }

    const userData = response.data;

    const widgetApi = await waitForWidgetReady();

    // Identify the user with secure hash
    await (widgetApi as unknown as ChatWidgetWithSetUser).setUser({
      userId: userData.userId,
      userHash: userData.userHash,
      email: userData.email,
      name: userData.displayName ?? userData.email,
    });
  } catch (error) {
    console.error("Error initializing ClickConnector widget:", error);
  }
};

/**
 * Show the ClickConnector widget
 */
export const showClickConnectorWidget = (): void => {
  try {
    const widgetApi = getWidgetApi();
    if (!widgetApi) return;
    widgetApi.setWidgetVisibility(true);
  } catch (error) {
    console.error("Error showing ClickConnector widget:", error);
  }
};

/**
 * Hide the ClickConnector widget
 */
export const hideClickConnectorWidget = (): void => {
  try {
    const widgetApi = getWidgetApi();
    if (!widgetApi) return;
    widgetApi.setWidgetVisibility(false);
  } catch (error) {
    console.error("Error hiding ClickConnector widget:", error);
  }
};

/**
 * Clear user identification (call on logout)
 */
export const clearClickConnectorUser = (): void => {
  try {
    // As of @clickconnector/widget-sdk v1.0.0 (checked 2024-06), there is no documented method to clear user state.
    // Only hiding the widget is possible. Revisit if the SDK is updated with a clearUser/logout method.
    const widgetApi = getWidgetApi();
    if (!widgetApi) return;
    widgetApi.setWidgetVisibility(false);
  } catch (error) {
    console.error("Error clearing ClickConnector user:", error);
  }
};
